import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import { UnsavedChangesGuard } from '@RichTextEditor';
import { FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
	EdFormControlLabel,
	EdFormControlError,
} from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTypedSelector } from '@Features/store';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { Typography } from '@eduact/ed-system';
import {
	Button,
	Checkbox,
	InputAdornment,
	ListItemText,
	MenuItem,
} from '@mui/material';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import TagChip from '@Components/UI/Chips/TagChip/TagChip';
import AddTag from '../../../Components/AddTag';
import { TypedTestQuestionPayload } from '../../../Types/TestQuestion';
import { ESSAY_SCHEMA } from '../../../Schema/Essay.schema';
import { useSnackbar } from '@Providers/useSnackbar';
import { useGotoParentRoute } from '@Providers/Routes/RoutesProvider';
import { TestQuestionsRequester } from '../../../Services/TestQuestions/TestQuestionsRequester';
import { useHistory } from 'react-router-dom';
import AnswerSchemaList from '../../../Components/AnswerSchemaList';

const EditEssayQuestionInfo = () => {
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();
	const goToParent = useGotoParentRoute();
	const [addedId, setAddedId] = useState<number | undefined>();
	const editorRef = useRef<unknown | null>(null);
	const feedbackEditorRef = useRef<unknown | null>(null);

	const { currentEntity } = useTypedSelector((state) => state.Tests);
	const { dropdownTags } = useTypedSelector((state) => state.dropdowns);
	const { currentEntity: currentQuestion } = useTypedSelector(
		(state) => state.TestQuestions
	);

	const { control, watch, formState, handleSubmit, setValue } = useForm<
		TypedTestQuestionPayload<'essay'>
	>({
		mode: 'all',
		resolver: yupResolver(ESSAY_SCHEMA),
		defaultValues: {
			...currentQuestion,
			test_id: currentEntity?.id,
			tags: currentQuestion?.tags?.map((q) => q.id),
			type: 'essay',
		},
	});

	const { isValid, isDirty, isSubmitting } = formState;
	const watchTags = watch('tags');

	useEffect(() => {
		if (addedId !== undefined) {
			setValue('tags', [...watchTags, addedId]);
		}
	}, [addedId]);

	const onSave = async (form: TypedTestQuestionPayload<'essay'>) => {
		try {
			await TestQuestionsRequester.instance.update(form);
			displaySnackbar('success', 'Updated question successfully');
			history.goBack();
		} catch (error) {
			displaySnackbar('error', "Couldn't update question");
		}
	};

	return (
		<FlexLayout flexDirection="column" px="2.625rem" flex="1">
			<UnsavedChangesGuard formState={formState} />
			<FlexLayout flex="1">
				<Controller
					control={control}
					name="content"
					render={({
						field: { value, onChange, ...props },
						fieldState: { error },
					}) => {
						return (
							<FlexLayout flexDirection={'column'} width="100%">
								<RichTextEditor preset="question"
									{...props}
									disabled={currentEntity?.active}
									value={value}
									onChange={onChange}
								/>
								<EdFormControlError error={error}>
									{error?.message}
								</EdFormControlError>
							</FlexLayout>
						);
					}}
				/>
			</FlexLayout>
			<Spacer my="1.5rem" />
			<GridLayout gridTemplateColumns={'repeat(1,1fr)'} gridGap="1.875rem">
				<FlexLayout alignItems="center" mb="2rem">
					<AnswerSchemaList control={control} />
					<Spacer mx="0.5rem" />
				</FlexLayout>
				<FlexLayout alignItems="center">
					<EdFormControl
						width="5rem"
						type="number"
						control={control}
						name="weight"
						disabled={currentEntity?.active}
						label="Weight"
					/>
					<Spacer mx="0.5rem" />
					<Typography fontWeight="600">Points</Typography>
				</FlexLayout>
				<FlexLayout gridGap="1rem">
					<EdFormControl
						disabled={currentEntity?.active}
						label="Tags"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<EdIcon mIconType="Regular">local_offer</EdIcon>
								</InputAdornment>
							),
						}}
						SelectProps={{
							multiple: true,
							renderValue: (value) => {
								const values = Array.from(value as number[]);
								return (
									<>
										{values?.map((v) => {
											const tag = dropdownTags?.find((_) => _.id === v);
											return <>{tag?.name},</>;
										})}
									</>
								);
							},
						}}
						control={control}
						name="tags"
						select
						multiple
						renderValues={(value) => {
							return (
								<>
									<Spacer mb="1rem" />
									<FlexLayout
										overflow="auto"
										py={'0.4rem'}
										flexWrap="wrap"
										gridGap="0.5rem"
										width="18.75rem"
									>
										{dropdownTags
											?.filter((_) => watchTags?.includes(_.id))
											.map((tag) => {
												return (
													<TagChip
														bgColor={tag?.theme}
														icon={<EdIcon>local_offer</EdIcon>}
														label={tag?.name}
													/>
												);
											})}
									</FlexLayout>
								</>
							);
						}}
					>
						{dropdownTags?.map((tag, index) => {
							return (
								<MenuItem value={tag?.id} key={`tag-${tag?.id}`}>
									<Checkbox
										sx={{ color: tag?.theme }}
										checked={watchTags?.includes(tag?.id)}
									/>
									<ListItemText primary={tag?.name} />
								</MenuItem>
							);
						})}
					</EdFormControl>
					{!currentEntity?.active && <AddTag setAddedId={setAddedId} />}
				</FlexLayout>
				<FlexLayout>
					<EdFormControlLabel>Feedback</EdFormControlLabel>
					<Controller
						control={control}
						name="feedback"
						render={({ field: { onChange, value, ...props }, fieldState }) => {
							return (
								<RichTextEditor preset="feedback"
									{...props}
									min_height={200}
									disabled={currentEntity?.active}
									value={value}
									onChange={onChange}
								/>
							);
						}}
					/>
				</FlexLayout>
			</GridLayout>
			<FlexLayout mt="1.688rem" justifyContent="flex-end">
				<Button
					// disabled={!formState.isDirty}
					variant="contained"
					color="warning"
					onClick={() => goToParent()}
				>
					Cancel
				</Button>
				<Spacer mx="0.5rem" />
				<Button
					onClick={handleSubmit(onSave)}
					disabled={!isDirty}
					variant="contained"
				>
					Save
				</Button>
			</FlexLayout>
		</FlexLayout>
	);
};

export default EditEssayQuestionInfo;
